import { DialogActions, DialogContent } from '@material-ui/core';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { noop } from 'lodash';
import { FC, useCallback, useState } from 'react';

import { Button } from 'src/general/components/Button/Button';
import { FormikInputLocation } from 'src/general/components/forms/FormikInput/FormikInputLocation';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { SetLocationPopupMicrocopyProps } from '../../SetLocationPopup.types';
import { LocationObject, LocationResponse } from '../SetLocation/SetLocation.types';
import classes from './EnterLocationForm.module.scss';
import { enterLocationValidationSchema } from './EnterLocationForm.types';

interface EnterLocationFormProps {
  microcopy: SetLocationPopupMicrocopyProps;
  onChange: (value: any) => void;
  searchLocation: (userInput: string) => Promise<LocationResponse>;
}

export const EnterLocationForm: FC<EnterLocationFormProps> = ({ microcopy, onChange, searchLocation }) => {
  const [location, setLocation] = useState<LocationObject | null>(null);
  const { buttonConfirm, label, exampleLocation } = microcopy;

  const searchLocationInner = useCallback(
    (value: string) =>
      searchLocation(value).then((data) => {
        if (data.length === 0) {
          pushToDataLayer({ event: 'set_handover_location_search_no_response', search_term: value });
        }

        return data;
      }),
    [searchLocation],
  );

  return (
    <div className={classes.formWrapper}>
      <Formik
        initialValues={{ location: '' }}
        validate={() =>
          enterLocationValidationSchema
            .validate({ location })
            .then(noop)
            .catch(() => ({ location: 'Enter a postcode or suburb, e.g. 2000 or Sydney' }))
        }
        onSubmit={() => {
          onChange(location);
          pushToDataLayer({
            event: 'set_handover_location_submit',
            options: locationToEventType(location!),
            location,
          });
        }}
      >
        <Form className={classes.formWrapper} autoComplete="off">
          <DialogContent>
            <FormikInputLocation
              testIdPrefix="LocationDropdown"
              name="location"
              label={label}
              helpText={exampleLocation}
              searchLocation={searchLocationInner}
              onFocus={() => pushToDataLayer({ event: 'set_handover_location_select_input_field' })}
              onLocationChange={(location) => {
                setLocation(location);
                pushToDataLayer({
                  event: 'set_handover_location_option_selected',
                  location: { suburb: location?.name, post_code: location?.post_code, state: location?.state },
                });
              }}
            />
          </DialogContent>
          <DialogActions>
            <div className={classNames(classes.fullWidth, classes.flexColumn)}>
              <Button type="submit" variant="primary" className={classes.button} data-testid="LocationDropdown-submit">
                {buttonConfirm}
              </Button>
            </div>
          </DialogActions>
        </Form>
      </Formik>
    </div>
  );
};

export const locationToEventType = ({
  // eslint-disable-next-line camelcase
  can_deliver,
  // eslint-disable-next-line camelcase
  can_pickup,
}: LocationObject) => {
  // eslint-disable-next-line camelcase
  if (can_deliver && can_pickup) {
    return 'pickup and delivery';
  }
  // eslint-disable-next-line camelcase
  if (can_pickup) {
    return 'pick up only';
  }

  return 'no options';
};
